import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { routes } from '_api';

import type { Login, LoginWithGoogleReq } from "_types/requests/auth";
import { currentUser, forgetPassword, getCourts, login, loginWithGoogle, logout, resetPassword, selectCourt, updateCourt } from "_api/fetchers/auth";
import { AuthService } from "_services/auth.service";


export function useLogin() {
    const mutationKey = [...routes.auth.login] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: Login) => {
            return login(mutationKey, payload);
        },

        onMutate: async d => {

        },
        onSuccess: async d => {
            AuthService.clearLoggedAs();
            AuthService.setToken(d.token, d.requiresTwoFactor);
        },
        onError: err => {

        },
    });
}

export function useLoginWithGoogle() {
    const mutationKey = [...routes.auth.google] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: LoginWithGoogleReq) => {
            return loginWithGoogle(mutationKey, payload);
        },

        onMutate: async d => {

        },
        onSuccess: async d => {
            AuthService.setToken(d.token, d.requiresTwoFactor);
            AuthService.clearLoggedAs();
        },
        onError: err => {
        },
    });
}

export function useCurrentUser() {
    const queryKey = [...routes.auth.currentUser] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: currentUser,
        staleTime: 1000 * 60 * 5,
    });

    const user = swr.data;
    AuthService.currentUser = user;
    AuthService.$currentUser.set(user);
    return { ...swr, user } as const;
}

export function useCurrentUserForProvider() {
    const queryKey = [...routes.auth.currentUser] as const;

    try {
        const swr = useQuery({
            queryKey,
            queryFn: currentUser,
            initialData: {} as any,
            enabled: !AuthService.$shouldSelectCourt.$
        });
        const user = swr.data;
        AuthService.currentUser = user;
        AuthService.$currentUser.set(user);
        return { ...swr, user };
    } catch (error) {
        return null;
    }
}

export function useLogout() {
    const mutationKey = [...routes.auth.logout] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return logout;
        },
        onMutate: async d => {

        },
        onSuccess: async d => {
            AuthService.logout();
        },
        onError: err => {

        },
    });
}

export function useResetPassword() {
    const mutationKey = [...routes.auth.resetPassword] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: { email: string, token: string, password: string }) => {
            return resetPassword(mutationKey, payload);
        },
        onMutate: async d => {

        },
        onSuccess: async d => {

        },
        onError: err => {

        },
    });
}

export function useForgetPassword() {
    const mutationKey = [...routes.auth.forgetPassword] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: { email: string, captcha: string }) => {
            return forgetPassword(mutationKey, payload);
        },
        onMutate: async d => {

        },
        onSuccess: async d => {

        },
        onError: err => {

        },
    });
}

export function useUserCourts() {
    const queryKey = [...routes.auth.courts] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: getCourts,
    });

    const courts = swr.data;
    return { ...swr, courts } as const;
}

export function useSelectCourt() {
    const mutationKey = [...routes.auth.selectCourt] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (courtId: string) => {
            return selectCourt(mutationKey, { courtId });
        },

        onMutate: async d => {

        },
        onSuccess: async d => {
            AuthService.setToken(d.token);
            AuthService.clearLoggedAs();
        },
        onError: err => {

        },
    });
}


export function useUpdateCourt() {
    const mutationKey = [...routes.auth.uppdateCourt] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (courtId: string) => {
            return updateCourt(mutationKey, { courtId });
        },

        onMutate: async d => {

        },
        onSuccess: async d => {
            AuthService.setToken(d.token);
        },
        onError: err => {

        },
    });
}
